import React, { useState } from 'react';
import { pagesData, companyInfo } from '../data/mock';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, FileText, Code, HelpCircle, ArrowLeft, ArrowRight, Clock, Sparkles, X, Coffee, Rocket, Star, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const sectionIcons = {
  'Blog': BookOpen,
  'Case Studies': FileText,
  'Documentation': Code,
  'Support': HelpCircle
};

// Rotating coming soon messages
const comingSoonMessages = [
  { icon: Coffee, title: "Brewing Something Special", message: "Our team is working hard to bring you amazing content. Grab a coffee and check back soon!" },
  { icon: Rocket, title: "Launching Soon", message: "This resource is on the launchpad. We're doing final checks before liftoff!" },
  { icon: Star, title: "Worth the Wait", message: "Good things come to those who wait. We're polishing this to perfection for you." },
  { icon: Heart, title: "Made with Love", message: "We're crafting this content with care. It'll be ready before you know it!" },
  { icon: Sparkles, title: "Magic in Progress", message: "Our wizards are working their magic. This resource will appear soon!" },
  { icon: BookOpen, title: "Chapter Loading", message: "This story is still being written. Come back soon to read the full tale!" }
];

const ResourcesPage = () => {
  const { resources } = pagesData;
  const [selectedItem, setSelectedItem] = useState(null);
  const [messageIndex, setMessageIndex] = useState(0);

  const handleItemClick = (item) => {
    // Rotate through messages
    setMessageIndex((prev) => (prev + 1) % comingSoonMessages.length);
    setSelectedItem(item);
  };

  const currentMessage = comingSoonMessages[messageIndex];
  const MessageIcon = currentMessage.icon;

  return (
    <div className="min-h-screen bg-[#050505] pt-20">
      <div className="orange-orb orange-orb-1 top-32 -left-32 w-[28rem] h-[28rem] z-0" />
      <div className="orange-orb orange-orb-2 top-1/2 -right-32 w-[32rem] h-[32rem] z-0" />
      <div className="relative z-10">
      <SEO
        title="Resources"
        description="AI insights, case studies, technical documentation, and guides from Agentic Atoms. Learn about AI automation, custom apps, and business transformation."
        path="/resources"
      />
      {/* Back button */}
      <div className="max-w-6xl mx-auto px-6 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 bg-white/[0.05] rounded-3xl flex items-center justify-center mx-auto mb-6"
          >
            <BookOpen className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {resources.title}
          </h1>
          <p className="text-[#A1A1AA] text-lg">
            {resources.subtitle}
          </p>
        </motion.div>

        {/* Coming Soon Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-12 bg-gradient-to-r from-[#0A0A0A] to-[#0A0A0A] rounded-2xl p-8 md:p-10 text-center border border-white/10"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            className="inline-block mb-4"
          >
            <Sparkles className="w-10 h-10 text-white" />
          </motion.div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Great Things Take Time
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto mb-2">
            We're crafting something special for you. Our resource library is being built with 
            the same care and precision we put into every AI solution.
          </p>
          <p className="text-[#A1A1AA] font-medium italic">
            "The best is yet to come — and it's coming soon."
          </p>
        </motion.div>

        {/* Resources Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {resources.sections.map((section, sectionIndex) => {
            const Icon = sectionIcons[section.title] || BookOpen;
            return (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + sectionIndex * 0.1 }}
                className="bg-white/[0.03] rounded-3xl p-8 relative"
              >
                {/* Coming Soon Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-white text-black text-xs rounded-full">
                  Coming Soon
                </div>
                
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{section.title}</h2>
                    <p className="text-sm text-[#A1A1AA]">{section.description}</p>
                  </div>
                </div>

                <div className="space-y-3 mt-6">
                  {section.items.map((item, itemIndex) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + sectionIndex * 0.1 + itemIndex * 0.05 }}
                      onClick={() => handleItemClick(item)}
                      className="bg-[#050505] rounded-xl p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow group"
                    >
                      <div>
                        <h3 className="font-medium text-white group-hover:text-[#A1A1AA] transition-colors">
                          {item.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="w-3 h-3 text-[#71717A]" />
                          <span className="text-xs text-[#71717A]">
                            Coming Soon
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#71717A] group-hover:text-white group-hover:translate-x-1 transition-all" />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}        
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-16 bg-white rounded-3xl p-10 md:p-14 text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Need Help Getting Started?
          </h3>
          <p className="text-[#71717A] mb-8 max-w-lg mx-auto">
            Our team is here to help you find the right solution for your business.
          </p>
          <Link
            to="/#contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#050505] text-white rounded-full font-medium hover:bg-white/[0.05] transition-colors"
          >
            Contact Us
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>

      {/* Coming Soon Modal */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedItem(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#050505] rounded-3xl p-8 max-w-md w-full text-center relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedItem(null)}
                className="absolute top-4 right-4 p-2 hover:bg-white/[0.05] rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-[#71717A]" />
              </button>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring' }}
                className="w-16 h-16 bg-white/[0.05] rounded-2xl flex items-center justify-center mx-auto mb-4"
              >
                <MessageIcon className="w-8 h-8 text-white" />
              </motion.div>

              <h3 className="text-xl font-bold text-white mb-2">
                {currentMessage.title}
              </h3>
              
              <p className="text-[#A1A1AA] mb-4">
                {currentMessage.message}
              </p>

              <div className="bg-white/[0.03] rounded-xl p-4 mb-6">
                <p className="text-sm text-[#A1A1AA] font-medium">
                  "{selectedItem.title}"
                </p>
                <p className="text-xs text-[#71717A] mt-1">
                  will be available soon
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedItem(null)}
                  className="flex-1 px-4 py-2.5 border border-white/10 rounded-full text-white font-medium hover:bg-white/[0.03] transition-colors"
                >
                  Got it
                </button>
                <Link
                  to="/#contact"
                  className="flex-1 px-4 py-2.5 bg-white text-black rounded-full font-medium hover:bg-[#222] transition-colors"
                  onClick={() => setSelectedItem(null)}
                >
                  Notify Me
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence></div>
    </div>
  );
};

export default ResourcesPage;
